/**
 * <CallPage>
 *
 * Route entry for `/call/:encounterId`. Post-refactor this is a thin shell:
 *   1. Resolves the patient + encounter + practitioner + organization + project
 *      from URL params and Medplum.
 *   2. Joins the Zoom session through `<CallSessionProvider>` (single owner of
 *      the Zoom client + scribe lifecycle — same provider the chart side-panel
 *      flow uses).
 *   3. Mounts `<EncounterWorkspace parentMode="page">` which composes the SOAP
 *      placeholder + `<ZoomPanel>` panel UI.
 *   4. Keeps the call-specific HealthGorilla (HG) place-order iframe modal and
 *      the appointment-consent download — those are NOT in the provider or
 *      the ZoomPanel.
 *
 * Removed from this file (now owned by `<CallSessionProvider>` + `<ZoomPanel>`):
 *   - `ZoomVideo.createClient()` allocation + `client` ref
 *   - `joinSession` / `leaveSession` / `renderVideo` / peer-video listener
 *   - `joinedRef` / `leavingRef` / `peerVideoStateHandlerRef`
 *   - local audio/video toggle state (`isAudioMuted`, `isVideoMuted`)
 *   - CameraButton / MicButton / FullScreenButton (legacy in-panel controls)
 *   - `<video-player-container>` hidden host + `containerUuid` regen
 *   - `isContentFull` / `expandViewToFullWidth` chrome toggles (the new ZoomPanel
 *     is right-aligned with its own resize handle; the SOAP pane is no longer
 *     toggled in/out as a sibling)
 */
import { ChartItem, itemsMap } from '@/pages/Patient/components/PatientChart.utils'
import { PatientChartLayout } from '@/pages/Patient/components/PatientChartLayout'
import { PatientChartSidePanelHost } from '@/pages/Patient/components/PatientChartSidePanelHost'
import { CHART_CONTENT_HEIGHT_VAR } from '@/pages/Patient/chartTopOffset'
import { parsePatientChartSplat } from '@/pages/Patient/patientChartPath'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useMedplum } from '@medplum/react'
import { useEffect, useRef, useState } from 'react'
import type { Encounter, Parameters, Patient, Practitioner } from '@medplum/fhirtypes'
import { showNotification } from '@mantine/notifications'
import { getConfig } from '@/config.ts'
import { EncounterWorkspace } from './EncounterWorkspace'
import { Modal } from '@mantine/core'
import { v4 } from 'uuid'
import { logError } from '@/utils/errorUtils'
import { useAppFlags } from '@/hooks/useAppFlags'
import { usePatientChartCounts } from '@/hooks/usePatientChartCounts'
import { useIsManager } from '@/hooks/useIsManager'
import { SoapNotePlanProvider } from './context/SoapNotePlanContext'
import { formatPractitionerDisplay } from '@/pages/Patient/components/progressNoteEncounter.utils'
import { useCallSession, type JoinSessionResult } from '@/components/session'
import { useInterruptedVisitRecovery } from './hooks/useInterruptedVisitRecovery'
import { useEncounterScribeState } from './hooks/useEncounterScribeState'
import type { DraftRecoveryRequest } from '@/components/scribe/DraftRecoveryNotice'
import { InterruptedResumePanel } from '@/components/scribe/InterruptedResumePanel'
import type { LiveCaption } from './hooks/useLiveTranscription'
import { useActiveScribeSession } from './hooks/useActiveScribeSession'
import type { ActiveScribeSession, ScribeChunk } from './hooks/useActiveScribeSession'

// Mirrors the middleware's `TELECONFERENCE_SESSION_EXTENSION_URL`
// (appointment.service.ts): the booking flow stamps this extension on the
// Encounter with a random UUID, and the patient portal joins the Zoom room
// named by that UUID. The doctor must join the SAME name to share the room.
const TELECONFERENCE_SESSION_EXTENSION_URL =
  'http://example.com/fhir/StructureDefinition/teleconference-session'

// Sticky-stack math is shared with `<PatientPage>` via the CSS-variable
// expression in `@/pages/Patient/chartTopOffset`:
//   64px Shell header + --recent-bar-height + --patient-header-height.
// The call route reuses the same action bar, so the chart sidebar and the
// right-pane scroll container reserve identical vertical space and reflow
// when the RECENT bar mounts/unmounts.

export function CallPage() {
  const params = useParams()
  const navigate = useNavigate()
  const medplum = useMedplum()
  const profile = medplum.getProfile()
  const project = medplum.getProject()
  const [patient, setPatient] = useState<Patient>()
  const { data: sectionCounts } = usePatientChartCounts(patient?.id)
  const [encounter, setEncounter] = useState<Encounter>()
  const [encounterId, setEncounterId] = useState<string>()
  const [hgKey, setHgKey] = useState('')
  const [isPlaceOrder, setIsPlaceOrder] = useState(false)
  const [hgIdentity, setHgIdentity] = useState('')
  const [patientDetailsKey, setPatientDetailsKey] = useState(v4())
  const [organizationId, setOrganizationId] = useState<string>()
  // When the AI scribe finalize flow calls `bridge.showSoapNote()`, this
  // flips true so the AI-populated `<ProgressNoteEdit>` takes over the main
  // pane (replacing the chart section view). The `<EncounterWorkspace>` is
  // mounted unconditionally so the form's RHF state survives the swap —
  // we only toggle visibility on the chart-section scroll container and
  // the EncounterWorkspace wrapper widths. Clicking a chart sidebar item
  // resets this back to false so the doctor can return to the chart view.
  const [showProgressNotePane, setShowProgressNotePane] = useState(false)

  // Single owner of the Zoom lifecycle. CallPage no longer auto-joins on
  // mount — the doctor must explicitly trigger a call via
  // "New Encounter → Virtual Visit" (which now flows through the shared
  // `<PatientChartSidePanelHost>` `onVirtualVisitSelect` override below into
  // `joinSession` for the current encounter). We still hold a ref to
  // `leaveSession` so that navigating away from `/call/:encounterId` tears
  // down any call that happens to be active.
  const {
    joinSession,
    leaveSession,
    scribeSessionId,
    markSessionActive,
    setScribeSessionId,
    setEncounterLanguage,
    isJoined,
  } = useCallSession()

  // Interrupted-visit recovery — prior transcript lines seeded into the
  // transcript pane on Resume, forwarded to <EncounterWorkspace> → <ZoomPanel>.
  const [recoveredCaptions, setRecoveredCaptions] = useState<LiveCaption[]>()
  // Bumped to trigger the partial-finalize flow inside <PageWorkspace> (which
  // owns the form bridge). Holds the recovered session id to adopt.
  const [finishRecovery, setFinishRecovery] = useState<{ sessionId: string; nonce: number } | null>(
    null,
  )

  // Suppresses a "Join Session" pre-join flash on the auto-join paths: on
  // `/call/:id?autoJoin=1` the auto-join effect (or the `intent=resume|finish`
  // hand-off) is about to run, so we start in the "connecting" state. Set true
  // in `performJoin`; cleared on a non-'joined' outcome and by the settle
  // effect below. Plain landings start false.
  const [joinConnecting, setJoinConnecting] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('autoJoin') === '1'
  })

  const { placeOrderV2 } = useAppFlags<{ placeOrderV2?: boolean }>()
  const config = getConfig()
  const hgUrl =
    config.baseUrl === 'https://api.behuman.health/'
      ? 'https://www.healthgorilla.com'
      : 'https://sandbox.healthgorilla.com'

  const hasFetchedHgKeyRef = useRef(false)
  useEffect(() => {
    if (placeOrderV2 === undefined) return
    if (!placeOrderV2 && !hasFetchedHgKeyRef.current) {
      hasFetchedHgKeyRef.current = true
      fetchHGKey()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeOrderV2])

  useEffect(() => {
    if (params.encounterId && params.encounterId !== encounterId) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setEncounterId(params.encounterId)
      // Fresh navigation to a different encounter — drop any recovery state
      // carried over from the previous encounter so its seeded transcript /
      // finish trigger can't bleed into the new one. (Resume's own
      // `setRecoveredCaptions` re-seeds afterward when the gate fires.) Without
      // this, switching encounters within the single CallPage instance left the
      // prior encounter's `recoveredCaptions`/`finishRecovery` in state.
      setRecoveredCaptions(undefined)
      setFinishRecovery(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  useEffect(() => {
    if (encounterId && encounterId !== encounter?.id) {
      medplum.readResource('Encounter', encounterId).then(async (res) => {
        const reference = res.subject?.reference?.split('/')

        if (!reference || reference?.length < 2) {
          showNotification({
            color: 'Red',
            message: 'Patient not defined',
          })
        }

        const [resourceType, resourceId] = reference || []

        if (resourceType !== 'Patient') {
          showNotification({
            color: 'Red',
            message: 'Patient not found',
          })
          return
        }

        const patientResource = await medplum.readResource('Patient', resourceId)
        setEncounter(res)
        setPatient(patientResource)

        const practitioner = res.participant?.find((participant) =>
          participant.individual?.reference?.match(/Practitioner\/.+/),
        )
        if (practitioner) {
          const practRole = await medplum.searchOne(
            'PractitionerRole',
            `practitioner=${practitioner.individual?.reference}`,
          )

          if (practRole?.organization) {
            const orgId = practRole.organization.reference?.split('/').pop()
            setOrganizationId(orgId)
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterId, medplum])

  const hasFetchedHgIdentityRef = useRef(false)
  useEffect(() => {
    if (placeOrderV2 === undefined) return
    if (placeOrderV2 || !patient) return
    if (hasFetchedHgIdentityRef.current) return
    hasFetchedHgIdentityRef.current = true

    const asyncFunction = async () => {
      const bot = await medplum.searchOne('Bot', 'name=get-hg-patient-global-id')
      if (!bot) {
        showNotification({
          color: 'Red',
          message: 'Cannot get patient global id',
        })
        return
      }

      if (!bot.id) {
        showNotification({
          color: 'Red',
          message: 'Cannot fetch patient global id',
        })
        return
      }

      const identity = await medplum.executeBot(bot.id, {
        patient,
      })

      if (!identity.hgPatientId) {
        const hgId = patient.identifier?.find(
          (identifier) =>
            identifier.use === 'usual' && identifier.system === 'https://www.healthgorilla.com',
        )?.value
        if (hgId) {
          setHgIdentity(hgId)
          return
        }
      }
      setHgIdentity(identity.hgPatientId)
    }

    void asyncFunction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, placeOrderV2])

  // Cleanup-only: when the user navigates away from `/call/:encounterId`
  // (e.g. back to the chart, another patient, the schedule), end any active
  // Zoom call so we don't leak the client / camera / mic.
  useEffect(() => {
    return () => {
      void leaveSession()
    }
    // `leaveSession` is a stable useCallback ref from the provider; depending
    // on it would re-trigger the cleanup on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-join on `?autoJoin=1` — the Resume/Finish hand-off from the interrupted-
  // visit recovery card. Fires once per mount; the query param is stripped after.
  const [searchParams, setSearchParams] = useSearchParams()
  const hasAutoJoinedRef = useRef(false)
  // Managers (care coordinators) never enter the interrupted-visit recovery
  // flow — they always plain-join. `loading` gates the mount effects so the
  // role resolves before the recovery probe can fire (fail-open: false).
  const { isManager, loading: managerLoading } = useIsManager()

  const buildJoinUserName = () => {
    const givenName = patient?.name?.[0]?.given?.[0] ?? ''
    const familyName = patient?.name?.[0]?.family ?? ''
    const providerName = profile?.name?.[0]
      ? `${profile.name[0].given?.[0] ?? ''} ${profile.name[0].family ?? ''}`.trim()
      : ''
    return providerName || [givenName, familyName].filter(Boolean).join(' ').trim() || 'Provider'
  }

  const performJoin = (
    activeEncounterId: string,
    resumeSessionId?: string,
  ): Promise<JoinSessionResult> => {
    if (!patient?.id) return Promise.resolve('failed')
    // Join the SAME Zoom room as the patient: booked encounters carry a
    // `teleconference-session` UUID extension; ad-hoc ones don't, so both sides
    // fall back to the encounter id (mirrors middleware `getSessionName`). The
    // id-match guard avoids an encounter-switch race using a stale extension.
    const sessionName =
      (encounter?.id === activeEncounterId
        ? encounter.extension?.find((ext) => ext.url === TELECONFERENCE_SESSION_EXTENSION_URL)
            ?.valueString
        : undefined) ?? activeEncounterId
    // Enter the "connecting" state so the pre-join prompt stays hidden while
    // the join is in flight. On a non-'joined' outcome ('failed' dead room →
    // degrades to the recovery card; 'busy' re-entrant no-op) clear it so the
    // doctor isn't stranded with neither a call nor a Join prompt. On 'joined'
    // the live panel takes over (`isJoined`), and the settle effect also clears.
    setJoinConnecting(true)
    return joinSession({
      encounterId: activeEncounterId,
      sessionName,
      userName: buildJoinUserName(),
      resumeSessionId,
    }).then((result) => {
      if (result !== 'joined') setJoinConnecting(false)
      // PD-1439 — another tab already holds this encounter. Nothing was
      // attempted, so this is guidance, not the retryable failure below.
      if (result === 'already-joined') {
        showNotification({
          color: 'yellow',
          message: 'You are already joined in another tab.',
        })
      }
      // Fresh-join failures land back on the pre-join prompt with no other
      // signal (resume failures surface via the recovery card instead).
      // Managers are excluded — their manual-note button is hidden.
      if (result === 'failed' && !resumeSessionId && !isManager) {
        showNotification({
          color: 'Red',
          message: "Couldn't join the call. You can retry, or write the note manually.",
        })
      }
      return result
    })
  }

  // Fresh-start join path (no recovery) — joins immediately. This is the
  // `onProceedFresh` callback for the recovery gate.
  const proceedWithJoin = (activeEncounterId: string) => {
    setRecoveredCaptions(undefined)
    void performJoin(activeEncounterId)
  }

  // Recovery probe helpers — also used directly by the chart-initiated
  // `intent=resume|finish` hand-off in the autoJoin effect below.
  const { getActiveSession, getChunks } = useActiveScribeSession()
  // What already exists for this encounter — a note (PD-1105), a generated draft
  // nobody opened, or just a transcript from a generation that failed. Drives
  // which recovery the rail offers instead of a bare rejoin.
  const {
    noteId: existingNoteId,
    isLocked: existingNoteLocked,
    state: scribeArtifactState,
    draftDocRefId,
    transcriptDocRefId,
    transcriptUrl,
    refresh: refreshScribeState,
  } = useEncounterScribeState(encounterId)

  // PD-1440 — when the note gets locked from another tab (visibility refetch or
  // a 409'd save), close the editor pane: nothing typed here can ever be saved,
  // and with the pane closed `baseIdle` recovers so the notice renders.
  useEffect(() => {
    if (existingNoteLocked && showProgressNotePane) {
      setShowProgressNotePane(false)
      showNotification({
        color: 'yellow',
        title: 'Note already locked',
        message: 'This visit was documented and locked in another tab.',
      })
    }
  }, [existingNoteLocked, showProgressNotePane])

  // Resume the recovered session: seed the prior transcript lines, then rejoin
  // the SAME per-encounter Zoom room ADOPTING the recovered scribe id (no fresh
  // /start). Returns 'failed' when the call already ended (degrade to Finish /
  // Discard), 'busy' on a re-entrant no-op (ignored upstream), 'joined' on
  // success. Lifted to a named const so the `intent=resume` hand-off can call
  // it directly without re-opening the modal gate.
  const doResume = async (
    activeEncounterId: string,
    session: ActiveScribeSession,
    chunks: ScribeChunk[],
  ): Promise<JoinSessionResult> => {
    setRecoveredCaptions(
      chunks.map<LiveCaption>((c) => ({
        text: c.text,
        speaker: c.speaker,
        timestamp: c.timestamp ?? new Date().toISOString(),
      })),
    )
    // PD-1600 — the adopted session keeps the language it was started with.
    setEncounterLanguage(session.language ?? 'en')
    return performJoin(activeEncounterId, session.sessionId)
  }

  // Finalize the recovered session WITHOUT joining Zoom: seat provider state,
  // reveal the Progress Note pane in-place, and trigger the partial-finalize
  // flow in <PageWorkspace>. `markSessionActive` seats `currentEncounterId`
  // (needed by `useFinalizeScribe`) and flips `isJoined`; it resets
  // `scribeSessionId`, so `setScribeSessionId` re-seats it for panel readers.
  // The partial-finalize passes `finishRecovery.sessionId` explicitly, so the
  // ordering is race-free. Lifted to a named const for the `intent=finish`
  // hand-off.
  const doFinish = (activeEncounterId: string, session: ActiveScribeSession): Promise<void> => {
    markSessionActive(activeEncounterId)
    setScribeSessionId(session.sessionId)
    setShowProgressNotePane(true)
    setFinishRecovery((prev) => ({
      sessionId: session.sessionId,
      nonce: (prev?.nonce ?? 0) + 1,
    }))
    // Synchronous, but the recovery hook's `onFinish` contract is Promise-returning.
    return Promise.resolve()
  }

  // Interrupted-visit recovery gate. Probes `/active` before any join /
  // auto-start. When a recoverable session exists for THIS encounter it opens
  // the modal and holds; otherwise it falls through to a fresh start.
  const recovery = useInterruptedVisitRecovery({
    onProceedFresh: proceedWithJoin,
    onResume: doResume,
    onFinish: doFinish,
    // Degrade-path discard exit: the call already ended and the doctor chose to
    // discard the captured transcript, so there's nothing left on this call
    // shell. Route back to the patient chart instead of stranding the doctor on
    // an empty call screen with no call, no transcript, and no next action.
    onExit: () => {
      if (patient?.id) {
        navigate(`/patients/${patient.id}`)
      } else {
        navigate('/')
      }
    },
  })

  // Single entry point for both auto-join and the chart action bar's
  // "Virtual Visit" override — runs the recovery probe first.
  const requestJoin = (activeEncounterId: string) => {
    if (isManager) {
      void performJoin(activeEncounterId)
      return
    }
    void recovery.requestJoin(activeEncounterId)
  }

  // Once a fresh scribe session is minted by this tab, record its id so a
  // re-probe (e.g. a later virtual-visit click in the same page session)
  // can't mistake our own active session for an interrupted one.
  const markSelfStarted = recovery.markSelfStarted
  useEffect(() => {
    markSelfStarted(scribeSessionId)
  }, [scribeSessionId, markSelfStarted])

  // Clear the "connecting" flag the moment we reach any settled state: the
  // recovery card is open, or the call joined. This covers the autoJoin +
  // recovery-cancel edge — there `joinConnecting` lazy-inits true, so without
  // this a dismissed recovery card would leave the pre-join prompt hidden.
  useEffect(() => {
    if (recovery.modalOpen || isJoined) {
      setJoinConnecting(false)
    }
  }, [recovery.modalOpen, isJoined])

  useEffect(() => {
    if (hasAutoJoinedRef.current) return
    if (!patient?.id || !encounterId) return
    if (searchParams.get('autoJoin') !== '1') return
    // Wait for the role to resolve so a manager never fires the recovery probe.
    if (managerLoading) return
    hasAutoJoinedRef.current = true

    // `intent` is the chart-initiated hand-off (the resume card on the patient
    // chart navigates here). It runs the recovery action DIRECTLY — skipping
    // the modal gate, since the doctor already chose Resume / Finish on the
    // chart. A normal side-panel hand-off has no `intent` and uses the gate.
    const intent = searchParams.get('intent')
    if (isManager) {
      proceedWithJoin(encounterId)
    } else if (intent === 'resume' || intent === 'finish') {
      void (async () => {
        const session = await getActiveSession(encounterId)
        if (!session || session.status !== 'live' || session.chunkCount === 0) {
          // The session is gone (finalized / discarded elsewhere).
          if (intent === 'finish') {
            // Nothing left to finalize — the doctor's intent was "wrap this
            // up", so do NOT spin up a fresh live call. Head back to the chart.
            navigate(patient?.id ? `/patients/${patient.id}` : '/')
          } else {
            // Resume with nothing recoverable — fall back to a fresh start.
            proceedWithJoin(encounterId)
          }
          return
        }
        if (intent === 'finish') {
          await doFinish(encounterId, session)
        } else {
          const chunks = await getChunks(encounterId, session.sessionId).catch(() => [])
          const result = await doResume(encounterId, session, chunks)
          if (result === 'failed') {
            // Dead Zoom room (the scribe session is still live in Redis, so the
            // rejoin failed but the transcript is recoverable). Surface the
            // recovery card so Finish / Discard are reachable instead of
            // stranding the doctor on an empty call shell with seeded captions
            // and no way forward. The card's degrade machine takes over from
            // here. (The direct hand-off skips the gate on the happy path; this
            // re-opens it only on failure.)
            void recovery.requestJoin(encounterId, { recoveryOnly: true })
          }
        }
      })()
    } else {
      requestJoin(encounterId)
    }

    // Strip `autoJoin` + `intent` so a manual reload doesn't re-trigger this.
    const next = new URLSearchParams(searchParams)
    next.delete('autoJoin')
    next.delete('intent')
    setSearchParams(next, { replace: true })
    // joinSession is a stable useCallback ref from the provider; depending on
    // it would re-trigger the effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id, encounterId, searchParams, setSearchParams, managerLoading, isManager])

  // `/call` self-recovery on a plain refresh. CallPage knows its own
  // `encounterId` from the URL (it survives a refresh), so it probes `/active`
  // directly — marker-independent — and opens the recovery choice when a live
  // session exists. `recoveryOnly` guarantees it never auto-starts a fresh call
  // when nothing is recoverable. Two guards keep it from racing the
  // chart-initiated `intent=resume` hand-off: `hasAutoJoinedRef` (that path
  // owns the mount) and `isJoined` (short-circuit once any join lands). The
  // effect deliberately does NOT depend on provider recovery state, so a late
  // re-hydration can't re-fire it mid-resume.
  const selfRecoveredRef = useRef(false)
  useEffect(() => {
    if (selfRecoveredRef.current) return
    if (hasAutoJoinedRef.current) return
    if (!patient?.id || !encounterId || isJoined) return
    if (searchParams.get('autoJoin') === '1') return
    // Managers skip the self-recovery probe.
    if (managerLoading || isManager) return
    selfRecoveredRef.current = true
    void recovery.requestJoin(encounterId, { recoveryOnly: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id, encounterId, isJoined, managerLoading, isManager])

  if (!patient) {
    return null
  }

  function fetchHGKey(callback?: () => void) {
    medplum
      .searchOne('Bot', 'name=HGJWT')
      .then(async (r) => {
        if (r?.id) {
          const parameters: Parameters = {
            resourceType: 'Parameters',
            parameter: [
              {
                name: 'jwtType',
                valueString: 'PREAUTH',
              },
            ],
          }
          const { signedToken } = await medplum.executeBot(r?.id, parameters)
          setHgKey(signedToken)
          if (callback) {
            callback()
          }
        } else {
          logError('call', 'Fetch HG key', new Error('Bot not found'))
        }
      })
      .catch((err) => logError('call', 'Fetch HG key', err))
  }

  const refetchPatient = async () => {
    if (!patient || !patient?.id) return

    const updatedPatient = await medplum.readResource('Patient', patient.id)
    setPatient(updatedPatient)
  }

  const onItemClick = (item: ChartItem) => {
    // Absolute navigation: when the URL holds a past-note docRefId segment
    // (e.g. `/call/:encounterId/:docRefId/progress-notes`), a relative
    // `navigate(item.dataKey)` only replaces the *last* segment which is a
    // no-op when the doctor re-clicks the same chart section to escape back
    // to the active encounter. Pinning to the absolute `/call/:encounterId/…`
    // path strips the docRefId and reveals the new-encounter edit form.
    navigate(`/call/${encounterId}/${item.dataKey}`)
    // Clicking any chart sidebar item returns to the chart-section view,
    // even if the AI-populated Progress Note pane is currently visible.
    setShowProgressNotePane(false)
  }

  // The call route is nested as `/call/:encounterId/*`. When `params['*']`
  // contains a `/`, the URL is `:docRefId/:section` and the doctor is
  // browsing a specific past document via `<Documents>` (rendered by the
  // chart-section component). In that case we suppress the new-encounter
  // Progress Note edit form on the right so the locked past note isn't
  // duplicated next to a fresh "New Progress Note" panel.
  const isBrowsingPastNote = (params['*'] || '').includes('/')

  const currentPage = parsePatientChartSplat(params['*']).section
  const currentItem = itemsMap.get(currentPage)

  /**
   * "New Encounter → Virtual Visit" override on /call/:encounterId.
   *
   * The shared `<PatientChartSidePanelHost>` forks here so the chart's action
   * bar can call `openPanel({ panelType: 'newEncounterSoap' })` route-
   * agnostically. On the /call route we're already at a specific encounter
   * (the URL one) — the doctor just hasn't joined Zoom yet. Instead of
   * spawning a sibling new-encounter flow, we start Zoom on the existing
   * encounter.
   *
   * Mirrors `SidePanelWorkspace`'s `joinSession(...)` shape at
   * EncounterWorkspace.tsx:262-266 — same session-name strategy (patient.id)
   * and same `userName` derivation. The only delta: we pass the REAL
   * `encounterId` from the URL so `<CallSessionProvider>.stampEncounterPeriod`
   * writes `period.start` to the correct FHIR Encounter (the side-panel flow
   * passes the patient id stand-in because it doesn't have a real Encounter
   * yet). Once `joinSession` flips `isJoined=true`, `<EncounterWorkspace
   * parentMode="page">`'s `<ZoomPanel>` (already mounted) renders the call.
   */
  const handleVirtualVisitSelect = (activeEncounterId: string) => {
    requestJoin(activeEncounterId)
  }

  // Page-mode "Join Session" pre-join surface (forwarded to <ZoomPanel> via
  // <EncounterWorkspace>). A doctor who lands on `/call/:encounterId` directly
  // (e.g. clicking a scheduled visit — no `?autoJoin=1`) sees the Zoom panel
  // with an explicit Join CTA instead of an empty slot. `onJoin` routes through
  // the SAME single entry as every other join (recovery probe → fresh join),
  // never `joinSession` directly. The prompt is mutually exclusive with
  // every other call state: hidden once joined, while the recovery card is
  // open, while the Progress Note pane is up, or while a join is in
  // flight (`joinConnecting`).
  //
  // `encounter?.id === encounterId` guards the encounter-SWITCH window: when
  // the route changes to a different `/call/:encounterId`, `encounter` state
  // briefly holds the previous encounter until the readResource effect
  // resolves, and a Join click in that window would read the WRONG
  // `teleconference-session` extension in `performJoin`. (First mount is
  // already safe — the page renders null until `patient` is set, and
  // patient + encounter commit in the same batch.)
  const onJoin = () => {
    if (encounterId) requestJoin(encounterId)
  }
  // PD-1105 — join prompt and existing-note notice are mutually exclusive; neither
  // shows until the note lookup resolves, so the join prompt never flashes first.
  const baseIdle =
    !isJoined &&
    !recovery.modalOpen &&
    !showProgressNotePane &&
    !joinConnecting &&
    encounter?.id === encounterId
  const showJoinPrompt = baseIdle && scribeArtifactState === 'none'
  const showExistingNote = baseIdle && scribeArtifactState === 'note'
  const onViewEditNote = () => {
    if (existingNoteId && patient?.id) {
      navigate(`/patients/${patient.id}/${existingNoteId}/progress-notes`)
    }
  }
  const onStartNewVisit = () => {
    if (patient?.id) navigate(`/patients/${patient.id}?newEncounter=1`)
  }

  // A draft or transcript with no note means the last attempt died before the
  // doctor ever saw it — offer the work back instead of a bare rejoin. The
  // action itself is added by `<EncounterWorkspace>`, which owns the form bridge;
  // `onRejoin` stays here because a failed note doesn't mean the visit is over
  // and this slot has replaced the join prompt.
  // PD-1378 — a generation in flight (this tab's End-Session, or another tab's)
  // outranks both: the rail states it and waits, so a second Retry can't stack a
  // duplicate run on top of the one that's already producing the note.
  const draftRecovery: DraftRecoveryRequest | null =
    baseIdle && scribeArtifactState === 'generating'
      ? { mode: 'generating', transcriptUrl }
      : baseIdle && scribeArtifactState === 'draft' && draftDocRefId
        ? { mode: 'draft-ready', transcriptUrl, draftDocRefId }
        : baseIdle && scribeArtifactState === 'transcript' && transcriptDocRefId
          ? { mode: 'transcript-only', transcriptUrl, transcriptDocRefId, onRejoin: onJoin }
          : null

  // AI actions on the existing-note notice — unlocked notes only. A draft
  // superseded by a newer transcript is already nulled by
  // `useEncounterScribeState`, so "Load AI draft" can't resurrect a
  // half-visit note after a rejoin.
  const existingNoteAiArtifacts =
    showExistingNote && !existingNoteLocked && (draftDocRefId || transcriptDocRefId)
      ? { draftDocRefId, transcriptDocRefId, transcriptUrl }
      : null

  // The page header (`<PatientChartActionBar>`) is portaled to <body> from
  // inside `<PatientChartLayout>`. It inherits Shell's
  // `padding-right: var(--zoom-panel-width)` so the bar reflows when Zoom
  // opens. It uses `useScrollContext().stickyTop` for its sticky offset — on
  // the call route we want the default 64px (under the global header), NOT
  // the chart-content's `stickyTop: '0px'`. The layout enforces that split by
  // wrapping `ScrollContext.Provider` around only the scroll container
  // (`scrollContextScope="scroll-container-only"`).
  //
  // `scrollContainerId` intentionally OMITTED below: the call route no longer
  // uses an inner overflow-auto container for the chart section. The
  // document/window scrolls as a single surface, which matches the user's
  // "scroll the page" mental model and avoids the dual-scroll wheel-confusion
  // that left users stuck around the SUBJECTIVE header on a locked Progress
  // Note view. Without the id, `Documents.tsx#scrollToPanel`,
  // `LabResults#scrollTo…` and `ResourceTable`'s InfiniteScroll all fall back
  // to window scrolling — see those files for the documented fallback path.
  return (
    <PatientChartSidePanelHost
      patient={patient}
      activeEncounterId={encounterId}
      onVirtualVisitSelect={handleVirtualVisitSelect}
    >
      <PatientChartLayout
        patient={patient}
        sectionCounts={sectionCounts}
        currentPage={currentPage}
        onItemClick={onItemClick}
        onPatientEdited={refetchPatient}
        encounterId={encounterId}
        chartKey={patientDetailsKey}
        chartExtraClassName="z-20 w-[330px]"
        sidebarWidth={330}
        // 344px chart sidebar + dynamic `--zoom-panel-width` (only set while
        // ZoomPanel is mounted; 0px otherwise) — keeps the right pane
        // reflowing when the call ends and the panel unmounts, instead of
        // leaving a 272px dead gap.
        scrollContainerWidthSubtract={344}
        stickyTop="0px"
        scrollContextScope="scroll-container-only"
        chartPaneClassName="relative overflow-x-auto"
        chartPaneInnerClassName="w-full min-w-0"
        chartPaneStyle={{
          // Reserve at least the chart content area below the sticky chrome
          // (Shell header + RECENT bar + PatientChartActionBar) so short
          // content (e.g. just-mounted skeleton) still fills the viewport.
          // Tall content (a locked Progress Note with five expanded sections)
          // grows past this floor and the page itself scrolls — see the
          // `ScrollContext.Provider` placement comment above.
          minHeight: CHART_CONTENT_HEIGHT_VAR,
          // When the AI scribe finalize flow surfaces the Progress Note pane,
          // hide the chart-section scroll container (the form becomes the
          // dominant view). The container is hidden — not unmounted — so
          // chart-section state survives the swap.
          display: showProgressNotePane ? 'none' : undefined,
        }}
        chartPaneContent={
          currentItem && (
            <currentItem.component
              refetchPatient={refetchPatient}
              patient={patient}
              item={currentItem}
            />
          )
        }
        flexWrapper={(children) => (
          <SoapNotePlanProvider>
            <div className="ml-[330px] flex" style={{ width: 'calc(100% - 330px)' }}>
              {children}
            </div>
          </SoapNotePlanProvider>
        )}
        rightPane={
          patient.id && encounter?.id && profile?.id && project?.id ? (
            // EncounterWorkspace is always mounted so the form's RHF state
            // (including the AI-populated draft pushed via
            // `<ScribeFormBridgeContext>.populateFromAi`) survives across
            // the chart/Progress-Note pane swap. Width is driven by
            // `showProgressNotePane`: full-flex when the AI draft is up,
            // 0px otherwise. The form keeps living in the DOM (so the
            // bridge keeps receiving updates) but is clipped to 0 width
            // when hidden via `overflow: hidden`.
            //
            // `visibility` is intentionally NOT used — it would propagate
            // into the EncounterWorkspace's `<ZoomPanel>` descendant (a
            // `position: fixed` element) and hide the active call panel
            // along with the form. Clipping via `overflow: hidden` plus a
            // 0-width flex slot leaves the fixed ZoomPanel rendered at
            // its own coords, independent of this wrapper.
            <div
              style={{
                flex: showProgressNotePane ? 1 : '0 0 0px',
                minWidth: 0,
                overflowX: 'hidden',
                overflowY: showProgressNotePane ? 'auto' : 'hidden',
                height: CHART_CONTENT_HEIGHT_VAR,
              }}
              data-testid="call-progress-note-pane"
            >
              <EncounterWorkspace
                parentMode="page"
                patientId={patient.id}
                encounterId={encounter.id}
                practitionerId={profile.id}
                practitionerDisplay={formatPractitionerDisplay(profile as Practitioner)}
                organizationId={organizationId || ''}
                projectId={project.id}
                onFinish={() => {
                  setPatientDetailsKey(v4())
                  setShowProgressNotePane(false)
                  // A note may exist now, which would make a cached
                  // draft/transcript verdict re-offer recovery for a
                  // documented visit.
                  refreshScribeState()
                }}
                onShowSoapNote={() => {
                  // Bug-2 fix (LD-21 follow-up): the doctor reached the
                  // "Create Progress Note" finalize step while a past-note
                  // URL was active (e.g. `/call/:encId/:docRefId/progress-notes`),
                  // which sets `isBrowsingPastNote=true` and passes
                  // `hideEditForm=true` to `<EncounterWorkspace>`. Flipping
                  // `showProgressNotePane` alone is not enough — the edit
                  // form simply isn't mounted in that branch, so the swap
                  // reveals an empty pane.
                  //
                  // The URL is the source of truth for "what view am I in".
                  // Strip the docRefId segment so the next render flips
                  // `isBrowsingPastNote` back to false, `<EncounterWorkspace>`
                  // mounts `<ProgressNoteEdit>`, and the AI draft (already
                  // cached in `<ScribeFormBridgeContext>` via the new pending-
                  // draft slot) flushes into the form as soon as it registers
                  // its `populateFromAi` callback.
                  if (isBrowsingPastNote && encounterId) {
                    navigate(`/call/${encounterId}`)
                  }
                  setShowProgressNotePane(true)
                }}
                hideEditForm={isBrowsingPastNote}
                recoveredCaptions={recoveredCaptions}
                finishRecovery={finishRecovery}
                showJoinPrompt={showJoinPrompt}
                joinConnecting={joinConnecting}
                onJoin={onJoin}
                showExistingNote={showExistingNote}
                isExistingNoteLocked={existingNoteLocked}
                onViewEditNote={onViewEditNote}
                onStartNewVisit={onStartNewVisit}
                draftRecovery={draftRecovery}
                existingNoteAiArtifacts={existingNoteAiArtifacts}
                onSaveConflict={refreshScribeState}
                onDraftAlreadyGenerating={refreshScribeState}
                onCancel={() => {
                  // #7 — the in-call edit→view transition. Routing it through
                  // `<ProgressNoteEdit onCancel>` means the doctor's exit goes
                  // through that component's block-on-fail `handleCancel`
                  // (awaited `flushPendingSave`; on failure it surfaces a red
                  // toast and keeps the doctor in the editor instead of
                  // calling back here). So this only fires once the latest
                  // edits are persisted to the draft blob — Lock can no longer
                  // commit stale content from the in-call flow.
                  setShowProgressNotePane(false)
                }}
              />
            </div>
          ) : undefined
        }
      />

      {!placeOrderV2 && (
        <Modal
          title="Place Order"
          opened={isPlaceOrder}
          size="1000"
          onClose={() => setIsPlaceOrder(false)}
        >
          <div style={{ position: 'relative' }}>
            <iframe
              id="hg-iframe"
              style={{ width: '100%', height: 'calc(100vh - 63px)' }}
              height={900}
              src={`${hgUrl}/prauth/login?assertion=${hgKey}&redirect_uri=${hgUrl}/clinicalnetwork#new/order/lab&patientIds=${hgIdentity}`}
            />
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                background: '#fff',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: '#fff',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '4px',
                left: 0,
                width: '65px',
                height: '60px',
                background: '#fff',
              }}
            />
          </div>
        </Modal>
      )}

      {recovery.modalOpen && recovery.recoveredSession && (
        // Unified recovery UI: the `/call` surface now renders the same
        // in-panel resume card as the patient chart (the old centered
        // InterruptedVisitModal is retired). Driven by the recovery hook's
        // proven gate/state machine; the chart card is driven by provider
        // `interruptedSession`. Same component, two drivers, different routes.
        <InterruptedResumePanel
          session={recovery.recoveredSession}
          callEnded={recovery.callEnded}
          busy={recovery.busy}
          onResume={recovery.resume}
          onFinish={recovery.finish}
          onDiscard={recovery.discard}
        />
      )}
    </PatientChartSidePanelHost>
  )
}
